/**
 * 할로윈 2026 홈 히어로 낙하 오버레이 — 호박 · 유령 · 사탕 (#6061 H4).
 *
 * 디자인 정본: Figma `Halloween 2026 — Home Overlay` (`31438:7390`) 「오브젝트 · 전역 · 움직임」 표 · 값과 로직은
 * 디자인 핸드오프 패키지(리포 밖)의 설정 · 오버레이 스크립트를 옮겼다(구조 = 예전 히어로 눈 효과).
 *
 * ```
 *   호박  굴러 떨어짐  5 / 3개  34–52px  .95  ×1.0
 *   유령  일렁이며     4 / 2개  40–56px  .88  ×1.35 (느리게)
 *   사탕  회전         9 / 4개  18–26px  .92  ×0.85
 *   낙하  12–18초 · 히어로 60% 에서 사라짐 · 55–85% 구간 페이드 · 모바일(<1024) 크기 ×0.75
 *   깊이  가까울수록 크고 빠르고 선명 · 먼 것은 최대 1.2px 흐림
 *   시작  이미지 준비 후 0.9초 페이드인 · 35% 는 이미 떨어지는 중 · 나머지는 순차 진입
 *   reduced-motion  표시 안 함
 * ```
 *
 * 분포가 뭉치거나 반복돼 보이지 않게 — ① 한 번 떨어지면 x·크기·깊이·흔들림을 새로 뽑고 ② x 는 최근 출발점과
 * 먼 곳(후보 10개 중 best-candidate) ③ 깊이는 층화 셔플(고르게 섞임).
 */

type Motion = "tumble" | "float" | "spin";

export type OverlayObject = {
  id: string;
  src: string;
  motion: Motion;
  count: { desktop: number; mobile: number };
  size: readonly [number, number];
  opacity: number;
  durationScale: number;
};

export const HALLOWEEN_2026_OVERLAY = {
  objects: [
    {
      id: "pumpkin",
      src: "/images/event/halloween-2026-sprite-pumpkin@2x.png",
      motion: "tumble",
      count: { desktop: 5, mobile: 3 },
      size: [34, 52],
      opacity: 0.95,
      durationScale: 1.0,
    },
    {
      id: "ghost",
      src: "/images/event/halloween-2026-sprite-ghost@2x.png",
      motion: "float",
      count: { desktop: 4, mobile: 2 },
      size: [40, 56],
      opacity: 0.88,
      durationScale: 1.35,
    },
    {
      id: "candy",
      src: "/images/event/halloween-2026-sprite-candy@2x.png",
      motion: "spin",
      count: { desktop: 9, mobile: 4 },
      size: [18, 26],
      opacity: 0.92,
      durationScale: 0.85,
    },
  ] as OverlayObject[],
  global: {
    duration: [12, 18] as const, // 낙하 시간 (초)
    ease: "cubic-bezier(0.22, 0.61, 0.36, 1)",
    endAt: 0.6, // 히어로 높이 60% 에서 사라짐
    fade: [0.55, 0.85] as const, // 페이드 구간 (낙하 진행률)
    drift: 120, // 좌우 흔들림 px
    windLeft: 0.7, // 왼쪽 바람 확률
    mobileScale: 0.75,
    depth: 0.6, // 깊이감 (가까울수록 크고 빠르고 선명)
    depthBlur: 1.2, // 가장 먼 입자 흐림 px
    prefill: 0.35, // 처음에 무작위 위치로 미리 채우는 비율 (나머지는 순차 진입)
    fadeIn: 900,
    breakpoint: 1024,
  },
};

type Global = typeof HALLOWEEN_2026_OVERLAY.global;

/** 스프라이트 표식 — 테스트 · 디버그에서 찾는다 */
export const OVERLAY_SPRITE_ATTR = "data-hw26-sprite";

const rand = (a: number, b: number) => a + Math.random() * (b - a);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/** 층화 셔플 백 — n 칸 [i, i+1)/n 에서 하나씩 · 다 쓰면 다시 섞음 */
const stratBag = (n: number) => {
  let bag: number[] = [];
  return () => {
    if (!bag.length) {
      bag = Array.from({ length: n }, (_, i) => (i + Math.random()) / n);
      for (let i = bag.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [bag[i], bag[j]] = [bag[j], bag[i]];
      }
    }
    return bag.pop() as number;
  };
};

/** 낙하 키프레임 — 진행률 [0, .05, .2, fade0, fade1, 1] · 모션별 x 흔들림 · 회전 · 크기 */
export function fallKeyframes(
  ob: OverlayObject,
  G: Global,
  endY: number,
  oMul: number,
): Keyframe[] {
  const T = [0, 0.05, 0.2, G.fade[0], G.fade[1], 1];
  const dir = Math.random() < G.windLeft ? -1 : 1;
  const d = rand(0.4, 1) * G.drift * dir;
  const o = ob.opacity * oMul;
  let x: number[];
  let r: number[];
  let s: number[];
  if (ob.motion === "tumble") {
    x = [0, d * 0.1, d * 0.35, d * 0.6, d * 0.8, d];
    const r0 = rand(-30, 30);
    const sp = rand(60, 180) * dir;
    r = [0, 0.1, 0.3, 0.6, 0.8, 1].map((k) => r0 + sp * k);
    s = [0, 0.7, 1, 1, 0.9, 0.7];
  } else if (ob.motion === "float") {
    // 같은 자세 반복 방지 = 기본 각도 무작위
    x = [0, d * 0.3, -d * 0.2, d * 0.5, -d * 0.1, d * 0.4];
    const r0 = rand(-14, 14);
    r = [0, 6, -6, 6, -4, 0].map((v) => v + r0);
    s = [0.6, 0.9, 1, 1, 1.05, 1.1];
  } else {
    x = [0, d * 0.15, d * 0.4, d * 0.6, d * 0.8, d];
    const sp = rand(360, 720) * dir;
    r = [0, 0.05, 0.2, 0.55, 0.85, 1].map((k) => sp * k);
    s = [0, 0.8, 1, 1, 0.9, 0.6];
  }
  const op = [0, o, o, o, o * 0.5, 0];
  const xs = x[5] / 2; // 경로 중간이 뽑힌 x 에 오도록
  return T.map((t, i) => ({
    offset: t,
    opacity: op[i],
    transform: `translate(${(x[i] - xs).toFixed(1)}px, ${(endY * t).toFixed(1)}px) rotate(${r[i].toFixed(1)}deg) scale(${s[i]})`,
  }));
}

/**
 * `host`(히어로를 덮는 빈 레이어 — `position: absolute; inset: 0`)에 스프라이트를 떨어뜨린다. 돌려준 함수로 뗀다.
 *
 * - reduced-motion 이면 아무것도 붙이지 않는다(도중에 켜지면 전부 걷는다).
 * - 폭이 바뀔 때만 다시 시작한다(200ms 디바운스) — 높이 변화는 다음 낙하부터 반영된다.
 * - 히어로가 화면 밖이면 낙하를 일시정지하고, 돌아오면 그 자리에서 이어 간다(스크롤을 내린 동안 안 보이는 애니메이션을 돌리지 않는다).
 */
export function mountHomeOverlay(
  host: HTMLElement,
  cfg = HALLOWEEN_2026_OVERLAY,
): () => void {
  if (typeof window.matchMedia !== "function") return () => {};
  const rm = window.matchMedia("(prefers-reduced-motion: reduce)");
  const G = cfg.global;

  let gen = 0;
  let width = 0;
  let resizeT: ReturnType<typeof setTimeout> | undefined;
  let running = false;
  const timers = new Set<ReturnType<typeof setTimeout>>();
  const anims = new Map<HTMLElement, Animation>();
  // 순차 진입을 기다리는 낙하 — 화면에 돌아와도 자기 차례 전엔 재생하지 않는다
  const pending = new Set<Animation>();
  let visible = true;
  const recent: { x: number; t: number }[] = [];

  // 블루 노이즈 x — 최근 출발점(18개)과 거리 + 시간 경과 점수
  const pickX = (W: number) => {
    const now = performance.now();
    const tau = 1000 * G.duration[0];
    let best = 0;
    let bestScore = -1;
    for (let k = 0; k < 10; k++) {
      const x = rand(-0.03, 1.03) * W;
      let sc = Infinity;
      for (const r of recent) {
        sc = Math.min(sc, Math.abs(x - r.x) / W + ((now - r.t) / tau) * 0.5);
      }
      if (sc > bestScore) {
        bestScore = sc;
        best = x;
      }
    }
    recent.push({ x: best, t: now });
    if (recent.length > 18) recent.shift();
    return best;
  };

  type Ctx = { gen: number; mobile: boolean; W: number; nextZ: () => number };
  const spawn = (
    el: HTMLElement,
    ob: OverlayObject,
    ctx: Ctx,
    first: boolean,
  ) => {
    if (ctx.gen !== gen) return;
    const z = ctx.nextZ();
    const D = G.depth;
    const size =
      lerp(ob.size[0], ob.size[1], z) * (ctx.mobile ? G.mobileScale : 1);
    const speedK = lerp(1 - 0.35 * D, 1 + 0.15 * D, z); // 먼 것 = 느리게
    const oMul = lerp(1 - 0.45 * D, 1, z); // 먼 것 = 옅게
    const endY = host.clientHeight * G.endAt + size; // 매 낙하 시 실측 (높이 변화 반영)
    Object.assign(el.style, {
      width: `${size}px`,
      height: `${size}px`,
      top: `${-size}px`,
      left: `${pickX(ctx.W) - size / 2}px`,
      zIndex: String(Math.round(z * 100)),
      filter: `blur(${((1 - z) * G.depthBlur).toFixed(2)}px)`,
    });
    const dur = ((rand(...G.duration) * ob.durationScale) / speedK) * 1000;
    const a = el.animate?.(fallKeyframes(ob, G, endY, oMul), {
      duration: dur,
      easing: G.ease,
      fill: "both",
    });
    if (!a) return;
    anims.set(el, a);
    if (!visible) a.pause();
    if (first) {
      if (Math.random() < G.prefill) {
        a.currentTime = Math.random() * 0.85 * dur; // 미리 채움 — 무작위 위치
      } else {
        // 나머지 — 한 사이클 안에서 무작위 순차 진입
        a.pause();
        pending.add(a);
        const t = setTimeout(
          () => {
            timers.delete(t);
            pending.delete(a);
            if (ctx.gen === gen && visible) a.play();
          },
          Math.random() * dur * 0.8,
        );
        timers.add(t);
      }
    }
    a.onfinish = () => {
      a.cancel();
      spawn(el, ob, ctx, false);
    };
  };

  const clear = () => {
    gen++;
    timers.forEach(clearTimeout);
    timers.clear();
    anims.forEach((a) => {
      a.onfinish = null;
      a.cancel();
    });
    anims.clear();
    recent.length = 0;
    pending.clear();
    host.getAnimations?.().forEach((a) => a.cancel());
    host.replaceChildren();
  };

  const play = async () => {
    clear();
    const my = gen;
    width = host.clientWidth; // 먼저 기록 — ResizeObserver 첫 콜백이 재시작을 부르지 않게
    // 스프라이트 디코드 후 시작 — 첫 로드 때 한꺼번에 툭 나타나는 것 방지
    await Promise.all(
      cfg.objects.map((o) => {
        const i = new Image();
        i.src = o.src;
        return i.decode?.().catch(() => {});
      }),
    );
    if (my !== gen) return;
    host.animate?.([{ opacity: 0 }, { opacity: 1 }], {
      duration: G.fadeIn,
      easing: "ease-out",
    });
    const mobile = width < G.breakpoint;
    for (const ob of cfg.objects) {
      const n = ob.count[mobile ? "mobile" : "desktop"];
      const ctx: Ctx = {
        gen: my,
        mobile,
        W: width,
        nextZ: stratBag(Math.max(n, 3)),
      };
      for (let i = 0; i < n; i++) {
        const el = document.createElement("div");
        el.setAttribute(OVERLAY_SPRITE_ATTR, ob.id);
        el.style.position = "absolute";
        const img = new Image();
        img.alt = "";
        img.src = ob.src;
        Object.assign(img.style, {
          width: "100%",
          height: "100%",
          objectFit: "contain",
          display: "block",
        });
        el.appendChild(img);
        host.appendChild(el);
        spawn(el, ob, ctx, true);
      }
    }
  };

  const io =
    typeof IntersectionObserver === "undefined"
      ? null
      : new IntersectionObserver(([entry]) => {
          visible = entry.isIntersecting;
          anims.forEach((a) => {
            if (!visible) a.pause();
            else if (!pending.has(a)) a.play();
          });
        });

  const ro =
    typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver(() => {
          if (!running || host.clientWidth === width) return;
          clearTimeout(resizeT);
          resizeT = setTimeout(play, 200);
        });

  const start = () => {
    if (running || rm.matches) return;
    running = true;
    ro?.observe(host);
    io?.observe(host);
    void play();
  };
  const halt = () => {
    if (!running) return;
    running = false;
    clearTimeout(resizeT);
    ro?.disconnect();
    io?.disconnect();
    visible = true;
    clear();
  };
  const onRM = () => (rm.matches ? halt() : start());

  rm.addEventListener?.("change", onRM);
  start();
  return () => {
    rm.removeEventListener?.("change", onRM);
    halt();
  };
}
